/**
 * THE SHELL: which surface is in front of the player, and nothing else.
 *
 * Spec section 13.1 names six modes. This reducer owns exactly them and is pure: same state,
 * same event, same answer, and no field for time. The sea settles on its own clock whether a
 * sheet is open or not (spec 13.1: the shell "never pauses or changes paid-time simulation
 * semantics"), so the shell has no way to say "paused" - `shell.test.ts` pins the shape.
 *
 * SHEETS ARE A STACK OF FRAMES. Pause over the voyage, settings over pause: two frames, and
 * Back pops one. Each frame remembers the id of the control that opened it, so focus goes
 * back to THAT control and not to the top of whatever is underneath (`game-ui.ts` does the
 * focusing; this file only remembers where).
 *
 * `origin` is the surface under every sheet - the title or the voyage - and is what Back
 * restores when the last frame is popped.
 *
 * A SHEET OVER THE PAGE OF TYPE KEEPS THE PAGE. Opening the menu from the reading page pushes
 * a frame and leaves `mode` at `reading`, so popping it lands on the page the player was
 * reading and focus goes back to the control there that opened it. `sheetOpen` is the one
 * question "is a sheet on top" for both shapes (`sheet` over the picture, `reading` with
 * frames); the mode alone no longer answers it.
 */

export type ShellMode = 'boot' | 'title' | 'voyage' | 'sheet' | 'reading' | 'fatal-recovery';
export type SheetId = 'pause' | 'chart' | 'guide' | 'settings' | 'save' | 'offline' | 'ending' | 'credits' | 'fisher' | 'outfitter' | 'harbour';

export interface SheetFrame {
  readonly id: SheetId;
  /** The id of the element that opened this frame; focus returns to it on Back. */
  readonly returnFocusId: string;
}

export interface ShellState {
  readonly mode: ShellMode;
  readonly origin: 'title' | 'voyage';
  readonly sheets: readonly SheetFrame[];
}

export type ShellEvent =
  /** The page has its world and its save inspected; the title can show. */
  | { readonly kind: 'booted' }
  /** Continue Voyage or Set Sail: the real session is ticking. */
  | { readonly kind: 'start-voyage' }
  /** The voyage is stopped and saved; the title ticket is back over the preview. */
  | { readonly kind: 'return-to-title' }
  /** Push one sheet frame. `returnFocusId` is the control that asked for it. */
  | { readonly kind: 'open-sheet'; readonly id: SheetId; readonly returnFocusId: string }
  /** Pop exactly one frame, or leave the reading page. */
  | { readonly kind: 'back' }
  /** Drop every frame and return to the origin. */
  | { readonly kind: 'resume' }
  /** The plain-type page on or off. Only a voyage has one. */
  | { readonly kind: 'read'; readonly on: boolean }
  /** A startup, import or render failure: the recovery sheet, and nothing but a reload leaves it. */
  | { readonly kind: 'fatal' };

export const INITIAL_SHELL: ShellState = { mode: 'boot', origin: 'title', sheets: [] };

const TITLE: ShellState = { mode: 'title', origin: 'title', sheets: [] };
const VOYAGE: ShellState = { mode: 'voyage', origin: 'voyage', sheets: [] };
const READING: ShellState = { mode: 'reading', origin: 'voyage', sheets: [] };

export function reduceShell(state: ShellState, event: ShellEvent): ShellState {
  if (event.kind === 'fatal') {
    return state.mode === 'fatal-recovery' ? state : { mode: 'fatal-recovery', origin: state.origin, sheets: [] };
  }
  if (state.mode === 'fatal-recovery') return state;

  switch (event.kind) {
    case 'booted':
      return state.mode === 'boot' ? TITLE : state;
    case 'start-voyage':
      return state.mode === 'boot' ? state : VOYAGE;
    case 'return-to-title':
      return state.mode === 'boot' ? state : TITLE;
    case 'open-sheet': {
      if (state.mode === 'boot') return state;
      const sheets = [...state.sheets, { id: event.id, returnFocusId: event.returnFocusId }];
      return { mode: state.mode === 'reading' ? 'reading' : 'sheet', origin: state.origin, sheets };
    }
    case 'back': {
      if (state.mode === 'reading' && state.sheets.length === 0) return VOYAGE;
      if (state.sheets.length === 0) return state;
      const sheets = state.sheets.slice(0, -1);
      if (state.mode === 'reading') return { mode: 'reading', origin: 'voyage', sheets };
      return sheets.length === 0 ? (state.origin === 'title' ? TITLE : VOYAGE) : { mode: 'sheet', origin: state.origin, sheets };
    }
    case 'resume':
      if (state.mode === 'reading') return state.sheets.length === 0 ? VOYAGE : READING;
      if (state.sheets.length === 0) return state;
      return state.origin === 'title' ? TITLE : VOYAGE;
    case 'read':
      if (state.sheets.length > 0) return state;
      if (event.on) return state.mode === 'voyage' ? READING : state;
      return state.mode === 'reading' ? VOYAGE : state;
  }
}

/** Whether a sheet (or the recovery sheet) is on top of whatever surface is under it. */
export function sheetOpen(state: ShellState): boolean {
  return state.sheets.length > 0 || state.mode === 'fatal-recovery';
}

/** The frame on top, if any sheet is open. */
export function topSheet(state: ShellState): SheetFrame | null {
  return state.sheets[state.sheets.length - 1] ?? null;
}
